import Alert from "@mui/material/Alert";
import Logger from "../../../services/Logger";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import StepFooter from "../../pzl_common/board/StepFooter";
import PuzzleWorkflowStepper from "../../pzl_common/board/PuzzleWorkflowStepper";
import BorderHeader from "../../pzl_common/BorderHeader";
import StepPuzzleWordPanel from "../../pzl_common/board/StepPuzzleWordPanel";
import BorderLeftPanel from "../../pzl_common/BorderLeftPanel";
import AdminCrossBoardService, { DISABLED, REVIEWED } from "./AdminCrossBoardService";
import { usePuzzleData } from "./usePuzzleData";
import { AdminStoryPreviewTrigger } from "../../pzl_common/board/AdminStoryPreviewTrigger";
import { BuggleChartTrigger } from "../../../pages/components/ClusteredBuggleChartDialog";


export default function Step4Published() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [refresh, setRefresh] = useState(0);
    const { crossTpl: puzzle, loading, error: fetchError } = usePuzzleData(id, refresh);
    const [error, setError] = useState(null);

    // Loading states
    const [loadingReview, setLoadingReview] = useState(false);
    const [loadingDiscard, setLoadingDiscard] = useState(false);
    const [loadingDisable, setLoadingDisable] = useState(false);

    // Confirmation dialog state for discarding template
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    // Confirmation dialog state for disabling puzzle
    const [openDisableDialog, setOpenDisableDialog] = useState(false);
    // Confirmation dialog state for free toggle
    const [openFreeDialog, setOpenFreeDialog] = useState(false);
    const [pendingFreeValue, setPendingFreeValue] = useState(false);
    const [loadingFree, setLoadingFree] = useState(false);

    const handleDiscardTemplate = () => {
        // Show confirmation dialog instead of immediately discarding
        setOpenConfirmDialog(true);
    };

    const handleConfirmDiscard = async (targetId) => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingDiscard(true);
        
        try {
            setError(null);
            // Call API to mark template as discarded
            await AdminCrossBoardService.markAsDiscarded(targetId);
            // Show success message
            // Note: You might want to add a snackbar component here
            // Navigate back to puzzle list
            navigate("/admin/puzzle/cross_board");
        } catch (err) {
            Logger.error("Error discarding template:", err);
            setError("Failed to discard template. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingDiscard(false);
            setOpenConfirmDialog(false);
        }
    };

    const handleViewLive = () => {
        // TODO: Add view live puzzle functionality
        Logger.log("View live puzzle functionality to be implemented");
    };

    const handleReview = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingReview(true);
        
        try {
            setError(null);
            // Call API to change state back to REVIEWED
            await AdminCrossBoardService.updateCrossTplState(puzzle.id, REVIEWED);
            navigate(`/admin/puzzle/cross_board/reviewed/${puzzle.id}`);
        } catch (err) {
            Logger.error("Error updating puzzle state:", err);
            setError(err?.message || "Failed to move puzzle back to review. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingReview(false);
        }
    };

    const handleDisable = () => {
        // Show confirmation dialog instead of immediately disabling
        setOpenDisableDialog(true);
    };

    const handleConfirmDisable = async (targetId) => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingDisable(true);
        
        try {
            setError(null);
            // Call API to change state to DISABLED
            await AdminCrossBoardService.updateCrossTplState(targetId, DISABLED);
            // Refresh the puzzle data to show updated state
            setRefresh(prev => prev + 1);
        } catch (err) {
            Logger.error("Error disabling puzzle:", err);
            setError("Failed to disable puzzle. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingDisable(false);
            setOpenDisableDialog(false);
        }
    };

    const handleBack = () => {
        const fromStep = searchParams.get('fromStep');
        if (fromStep !== null) {
            navigate(`/admin/puzzle/cross_board?activeStep=${fromStep}`);
        } else {
            navigate("/admin/puzzle/cross_board");
        }
    };

    const handleFreeClick = (newFree) => {
        setPendingFreeValue(newFree);
        setOpenFreeDialog(true);
    };

    const handleConfirmFree = async () => {
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = "wait";
        setLoadingFree(true);
        try {
            setError(null);
            await AdminCrossBoardService.setFree(puzzle.id, pendingFreeValue);
            setRefresh((prev) => prev + 1);
            setOpenFreeDialog(false);
        } catch (err) {
            Logger.error("Error setting free:", err);
            setError("Failed to update free status. Please try again.");
        } finally {
            document.body.style.cursor = originalCursor || "default";
            setLoadingFree(false);
        }
    };

    // Parse puzzle grid from filledCoordinates
    const parsePuzzleGrid = () => {
        if (!puzzle?.filledCoordinates) return [];

        try {
            const filledCoordinates = JSON.parse(puzzle.filledCoordinates);
            return initializeSquare(filledCoordinates);
        } catch (error) {
            Logger.error('Error parsing filled coordinates:', error);
            return [];
        }
    };

    // Function to initialize the square array from filledCoordinates
    const initializeSquare = (filledCoordinates) => {
        const size = 12;
        const newSquare = Array.from({ length: size }, () => Array.from({ length: size }, () => ""));

        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                newSquare[row][col] = findCharacter(row, col, filledCoordinates);
            }
        }

        return newSquare;
    };

    const findCharacter = (row, col, filledCoordinates) => {
        if (filledCoordinates) {
            for (const coord of filledCoordinates) {
                const matchingPosition = coord.positions.find(
                    (p) => p.row === row && p.col === col
                );
                if (matchingPosition && matchingPosition.value) {
                    return matchingPosition.value.toUpperCase();
                }
            }
        }
        return "";
    };


    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
                p: 3
            }}>
                <Typography variant="h6">Loading puzzle data...</Typography>
            </Box>
        );
    }

    if (fetchError || !puzzle) {
        return (
            <Box sx={{ mt: 2, p: 3 }}>
                <Alert severity="error">
                    {fetchError || "Puzzle not found"}
                </Alert>
            </Box>
        );
    }


    return (
        <Box sx={{ width: "100%" }}>
            {/* Workflow Stepper */}
            <PuzzleWorkflowStepper
                currentState={puzzle?.state}
                puzzleId={puzzle?.id}
                size="small"
                align="left"
                onStepClick={() => setRefresh(prev => prev + 1)}
                puzzleType={puzzle?.type}
            />
            
            {/* Header with back button */}
            <BorderHeader
                onBack={handleBack}
                puzzle={puzzle}
                puzzleType="crossword"
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                {/* First Column: Puzzle Information */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <BorderLeftPanel
                        puzzle={puzzle}
                        onRefresh={() => setRefresh(prev => prev + 1)}
                        puzzleType="CROSSWORD"
                    />
                </Grid>

                {/* Second Column: Words List / Word Network */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <StepPuzzleWordPanel
                        puzzle={puzzle}
                        parsePuzzleGrid={parsePuzzleGrid}
                        publishedCrossTags={{
                            onSaved: () => setRefresh((p) => p + 1),
                            onError: (msg) => {
                                if (msg === null || msg === undefined) setError(null);
                                else setError(typeof msg === "string" ? msg : String(msg));
                            },
                        }}
                    />
                </Grid>
            </Grid>

            {/* Step Footer with Action Buttons */}
            <StepFooter
                stepType="published"
                title="Published & Live"
                description="This puzzle is currently live and available to users."
                onDiscard={handleDiscardTemplate}
                onViewLive={handleViewLive}
                onReview={handleReview}
                onDisable={handleDisable}
                loadingReview={loadingReview}
                loadingDiscard={loadingDiscard}
                loadingDisable={loadingDisable}
                puzzle={puzzle}
                onError={setError}
                onSuccess={(message) => {
                    Logger.log("Success:", message);
                }}
                free={puzzle?.free ?? puzzle?.isFree}
                onFreeClick={handleFreeClick}
                loadingFree={loadingFree}
                afterShare={
                    puzzle?.type === "CROSSWORD" ? (
                        <>
                            <BuggleChartTrigger templateId={puzzle.id} tplKind="cross" />
                            <AdminStoryPreviewTrigger
                                puzzle={puzzle}
                                onRefresh={() => setRefresh((prev) => prev + 1)}
                            />
                        </>
                    ) : null
                }
            />

            {/* Confirmation Dialog for Discarding Template */}
            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                title="Confirm Discard Template"
                message="Are you sure you want to discard this template? This action cannot be undone."
                targetName={`'${puzzle?.title || 'Untitled Template'}' will be discarded`}
                targetId={puzzle?.id}
                callback={handleConfirmDiscard}
            />

            {/* Confirmation Dialog for Disabling Puzzle */}
            <ConfirmDialog
                open={openDisableDialog}
                setOpen={setOpenDisableDialog}
                title="Confirm Disable Puzzle"
                message="Disable this puzzle? It will be withdrawn now. Scheduled for removal in 7 days (e.g. 6 days, 23 hours)."
                targetName={`'${puzzle?.title || 'Untitled Puzzle'}' will be disabled`}
                targetId={puzzle?.id}
                callback={handleConfirmDisable}
            />

            {/* Confirmation Dialog for Free toggle */}
            <ConfirmDialog
                open={openFreeDialog}
                setOpen={setOpenFreeDialog}
                title={pendingFreeValue ? "Set puzzle as Free" : "Set puzzle as Paid"}
                message={pendingFreeValue
                    ? "Are you sure you want to make this puzzle free for all users?"
                    : "Are you sure you want to make this puzzle paid?"}
                targetName={`'${puzzle?.title || 'Untitled Puzzle'}' will be ${pendingFreeValue ? 'free' : 'paid'}`}
                targetId={puzzle?.id}
                callback={handleConfirmFree}
            />
        </Box>
    );
}

